/**
 * Browser Chase Tracker
 * Manages order chasing in browser mode - monitors orderbook and repositions orders
 * 
 * Key features:
 * - Global singleton - persists across page navigation
 * - Tracks orders by exact size (immutable property)
 * - Uses BrowserPositionTracker for order verification
 * - Persists state to localStorage
 * - Dispatches events for UI updates
 */

export class BrowserChaseTracker {
    constructor() {
        this.activeChases = new Map(); // chaseId -> chaseState
        this.chaseHistory = []; // Completed/stopped chases
        this.orderbookConnections = new Map(); // asset -> subscription status
        this.monitoringIntervals = new Map(); // chaseId -> intervalId
        this.globalPollingInterval = null; // Global polling loop for all chases
        
        // Restore state from localStorage
        this.restoreState();

        // console.log('[CHASE-TRACKER] Initialized');
    }

    /**
     * Check if two sizes match within 1% tolerance
     * Handles edge case where size could be 0 or invalid
     */
    _sizesMatch(size1, size2) {
        const s1 = parseFloat(size1);
        const s2 = parseFloat(size2);
        if (s2 <= 0 || isNaN(s2)) return false;
        return Math.abs(s1 - s2) / s2 < 0.01;
    }

    /**
     * Start chasing an order
     * @param {string} orderId - Original order ID
     * @param {Object} orderData - Order details from positions tracker
     * @param {Object} settings - Chase settings from modal
     */
    async startChase(orderId, orderData, settings) {
        try {
            const chaseId = `chase-${orderId}-${Date.now()}`;

            // Normalize order data (handle both raw API format and transformed format)
            const normalizedData = {
                asset: orderData.coin || orderData.asset,
                side: orderData.side === 'BUY' ? 'B' : (orderData.side === 'SELL' ? 'A' : orderData.side),
                size: parseFloat(orderData.sz || orderData.size),
                price: parseFloat(orderData.limitPx || orderData.price)
            };

            // Determine direction for tracking
            const direction = normalizedData.side === 'B' ? 'LONG' : 'SHORT';

            console.log(`[CHASE-TRACKER] Starting chase ${chaseId}`, {
                orderId,
                asset: normalizedData.asset,
                side: normalizedData.side,
                size: normalizedData.size,
                price: normalizedData.price,
                settings: { tickDistance: settings.tickDistance, percentDistance: settings.percentDistance, isPercent: settings.isPercent, freqMin: settings.frequencyRangeMin, freqMax: settings.frequencyRangeMax }
            });

            console.log(`[CHASE-DEBUG] Step 1: Cancelling TP/SL orders...`);

            // Cancel any TP/SL orders for this position
            const cancelledTpSl = await this.cancelTpSlOrders(normalizedData.asset, normalizedData.side);
            console.log(`[CHASE-DEBUG] Step 1 done: Cancelled ${cancelledTpSl?.length || 0} TP/SL orders`);
            
            // Create chase state
            const chaseState = {
                chaseId: chaseId,
                originalOid: String(orderId),
                currentOid: String(orderId),
                oidHistory: [String(orderId)],
                asset: normalizedData.asset,
                side: normalizedData.side,
                direction: direction,
                size: normalizedData.size,
                originalSize: normalizedData.size,
                sizeHistory: [normalizedData.size],
                originalPrice: normalizedData.price,
                currentPrice: normalizedData.price,
                priceHistory: [normalizedData.price],
                fillEvents: [],
                missingCount: 0,
                settings: {
                    tickDistance: settings.tickDistance || (settings.isPercent ? null : 10),
                    percentDistance: settings.percentDistance || null,
                    isPercent: settings.isPercent || false,
                    frequencyRangeMin: settings.frequencyRangeMin || 10,
                    frequencyRangeMax: settings.frequencyRangeMax || 20,
                    rangePrice: settings.rangePrice || null,
                    rangeType: settings.rangeType || null,
                    anchorPrice: settings.anchorPrice || null,
                    useAnchor: settings.useAnchor || false,
                    tpslEnabled: settings.tpslEnabled || false,
                    tpAtrMultiple: settings.tpAtrMultiple || null,
                    slAtrMultiple: settings.slAtrMultiple || null,
                    aggressive: settings.aggressive || false  // Aggressive mode: cross spread for faster fills
                },
                status: 'active',
                lastModification: null,
                modificationCount: 0,
                startTime: Date.now(),
                lastCheck: Date.now(),
                cancelledTpSl: cancelledTpSl
            };
            
            // Store in active chases
            this.activeChases.set(chaseId, chaseState);
            console.log(`[CHASE-DEBUG] Step 2: Chase state created and stored`);

            // Save to localStorage
            this.saveState();
            console.log(`[CHASE-DEBUG] Step 3: State saved to localStorage`);

            // Initialize tick size from API metadata (more reliable than orderbook)
            console.log(`[CHASE-DEBUG] Step 4: Getting tick size from API...`);
            try {
                await window.BrowserHyperliquidClient.getAssetIndex(normalizedData.asset);
                // FIX: Read from asset-specific cache instead of shared instance variable
                // The shared variable (assetSzDecimals) can be overwritten by concurrent API calls,
                // causing a race condition where we get wrong tick size or undefined
                const cached = window.BrowserHyperliquidClient.assetIndexCache?.get(normalizedData.asset);
                const szDecimals = cached?.szDecimals;
                if (szDecimals !== undefined && szDecimals !== null) {
                    const priceDecimals = 6 - szDecimals;
                    chaseState.tickSize = Math.pow(10, -priceDecimals);
                    console.log(`[CHASE-TRACKER] Tick size from API for ${normalizedData.asset}: $${chaseState.tickSize} (szDecimals=${szDecimals})`);
                } else {
                    console.warn(`[CHASE-TRACKER] szDecimals not in cache for ${normalizedData.asset}, will use orderbook fallback`);
                }
            } catch (e) {
                console.warn(`[CHASE-TRACKER] Could not get tick size from API for ${normalizedData.asset}, will use orderbook fallback:`, e.message);
            }

            // Connect to orderbook for this asset
            console.log(`[CHASE-DEBUG] Step 5: Connecting to orderbook for ${chaseState.asset}...`);
            await this.connectOrderbook(chaseState.asset);
            console.log(`[CHASE-DEBUG] Step 5 done: Orderbook connected`);

            // Start monitoring loop (must await to ensure subscription completes)
            console.log(`[CHASE-DEBUG] Step 6: Starting monitoring loop...`);
            await this.startMonitoring(chaseId);
            console.log(`[CHASE-DEBUG] Step 6 done: Monitoring started`);

            // Start global polling loop (replaces delta + verification)
            console.log(`[CHASE-DEBUG] Step 7: Starting polling loop...`);
            this.startPolling();
            console.log(`[CHASE-DEBUG] Step 7 done: Polling active`);
            
            // Set up event listeners for instant fill/cancel detection
            const fillListener = (event) => {
                const { orderId } = event.detail;
                if (chaseState.oidHistory.includes(String(orderId))) {
                    // console.log(`[CHASE-TRACKER] 🎯 Order ${orderId} filled (detected via event)`);
                    this.stopChase(chaseId, 'order_filled');
                }
            };
            
            const cancelListener = (event) => {
                const { orderId } = event.detail;
                if (chaseState.oidHistory.includes(String(orderId))) {
                    // console.log(`[CHASE-TRACKER] 🎯 Order ${orderId} cancelled (detected via event)`);
                    this.stopChase(chaseId, 'order_cancelled');
                }
            };
            
            window.addEventListener('order-filled', fillListener);
            window.addEventListener('order-cancelled', cancelListener);
            
            // Store listeners for cleanup
            chaseState.fillListener = fillListener;
            chaseState.cancelListener = cancelListener;
            
            // Dispatch event for UI
            window.dispatchEvent(new CustomEvent('chase-started', {
                detail: {
                    chaseId: chaseId,
                    orderId: orderId,
                    orderData: orderData,
                    settings: settings
                }
            }));
            
            // console.log(`[CHASE-TRACKER] ✅ Chase started: ${chaseId}`);
            
            return { success: true, chaseId: chaseId };
            
        } catch (error) {
            // console.error('[CHASE-TRACKER] Error starting chase:', error);

            // Clean up partially initialized chase state
            const chase = this.activeChases.get(chaseId);
            if (chase) {
                // Unsubscribe from orderbook if subscribed
                if (chase.orderbookListener && window.ChaseOrderbookService) {
                    try {
                        await window.ChaseOrderbookService.unsubscribe(chase.asset, chase.orderbookListener);
                    } catch (unsubError) {
                        // Ignore unsubscribe errors during cleanup
                    }
                }

                // Remove event listeners if added
                if (chase.fillListener) {
                    window.removeEventListener('order-filled', chase.fillListener);
                }
                if (chase.cancelListener) {
                    window.removeEventListener('order-cancelled', chase.cancelListener);
                }

                // Remove from activeChases
                this.activeChases.delete(chaseId);

                // Update localStorage to remove partially saved state
                this.saveState();
            }

            return { success: false, error: error.message };
        }
    }

    /**
     * Stop chasing an order
     * @param {string} chaseId - Chase ID to stop
     * @param {string} reason - Reason for stopping
     */
    async stopChase(chaseId, reason = 'user_stopped') {
        const chase = this.activeChases.get(chaseId);
        if (!chase) {
            // console.warn(`[CHASE-TRACKER] Chase not found: ${chaseId}`);
            return { success: false, error: 'Chase not found' };
        }

        // Mutex: Prevent concurrent stop operations
        if (chase.isStopping) {
            return { success: true, alreadyStopping: true };
        }
        chase.isStopping = true;

        console.log(`[CHASE-TRACKER] Stopping chase ${chaseId}, reason: ${reason}`);

        // Place TP/SL orders when chase stops (if ATR multiples are configured)
        // Position verification inside placeTPSLOnFill() ensures TP/SL only placed if position exists
        // FIX: Add tpslPlaced flag to prevent duplicate TP/SL placement from race condition
        if (chase.settings?.tpslEnabled && chase.settings?.tpAtrMultiple && chase.settings?.slAtrMultiple) {
            if (!chase._tpslPlaced) {
                chase._tpslPlaced = true; // Set flag BEFORE async call to prevent race
                try {
                    await this.placeTPSLOnFill(chase);
                    console.log(`[CHASE-TRACKER] TP/SL placed for ${chase.asset}`);
                } catch (error) {
                    console.error(`[CHASE-TRACKER] Error placing TP/SL on fill:`, error);
                    chase._tpslPlaced = false; // Reset on error to allow retry
                }
            } else {
                console.log(`[CHASE-TRACKER] TP/SL already placed for ${chase.asset}, skipping duplicate`);
            }
        }

        // Update status
        chase.status = 'stopped';
        chase.endTime = Date.now();
        chase.endReason = reason;

        // Stop monitoring
        this.stopMonitoring(chaseId);
        
        // Move to history
        this.chaseHistory.push(chase);
        this.activeChases.delete(chaseId);
        
        // Disconnect orderbook if no other chases for this asset
        this.disconnectOrderbookIfUnused(chase.asset);
        
        // Save state
        this.saveState();
        
        // Dispatch event
        window.dispatchEvent(new CustomEvent('chase-stopped', {
            detail: {
                chaseId: chaseId,
                reason: reason,
                finalOid: chase.currentOid,
                modCount: chase.modificationCount
            }
        }));
        
        // console.log(`[CHASE-TRACKER] ✅ Chase stopped: ${chaseId}`);
        
        return { success: true };
    }

    /**
     * Connect to orderbook WebSocket for an asset
     * Uses dedicated ChaseOrderbookService (independent of terminal)
     */
    async connectOrderbook(asset) {
        // ChaseOrderbookService handles multiple subscriptions per asset automatically
        // No need to track connections - service does it internally
        // console.log(`[CHASE-TRACKER] Orderbook connection managed by ChaseOrderbookService for ${asset}`);
    }

    /**
     * Disconnect orderbook if no active chases need it
     * ChaseOrderbookService auto-cleanup when no listeners
     */
    disconnectOrderbookIfUnused(asset) {
        // ChaseOrderbookService automatically closes WebSocket when last listener unsubscribes
        // console.log(`[CHASE-TRACKER] Orderbook cleanup managed by ChaseOrderbookService for ${asset}`);
    }

    /**
     * Start monitoring loop for a chase
     */
    async startMonitoring(chaseId) {
        const chase = this.activeChases.get(chaseId);
        if (!chase) {
            console.error(`[CHASE-DEBUG] startMonitoring: chase ${chaseId} not found!`);
            return;
        }

        console.log(`[CHASE-DEBUG] startMonitoring: Setting up orderbook listener for ${chase.asset}`);

        // Create listener for this chase
        const orderbookListener = (data) => {
            // Data format: { asset, bids, asks, bestBid, bestAsk, timestamp }
            this.handleOrderbookUpdate(chaseId, data);
        };

        // Store listener reference for cleanup
        chase.orderbookListener = orderbookListener;

        // Subscribe to dedicated chase orderbook service
        if (!window.ChaseOrderbookService) {
            console.error('[CHASE-DEBUG] ❌ ChaseOrderbookService not available!');
            throw new Error('ChaseOrderbookService not initialized');
        }

        console.log(`[CHASE-DEBUG] Subscribing to ChaseOrderbookService for ${chase.asset}...`);
        await window.ChaseOrderbookService.subscribe(chase.asset, orderbookListener);

        console.log(`[CHASE-DEBUG] ✅ Subscribed to chase orderbook for ${chase.asset}`);
    }

    /**
     * Stop monitoring loop for a chase
     */
    async stopMonitoring(chaseId) {
        const chase = this.activeChases.get(chaseId);
        if (!chase) return;
        
        // Unsubscribe from chase orderbook service
        if (chase.orderbookListener) {
            await window.ChaseOrderbookService.unsubscribe(chase.asset, chase.orderbookListener);
            chase.orderbookListener = null;
            // console.log(`[CHASE-TRACKER] ✅ Unsubscribed from chase orderbook for ${chase.asset}`);
        }
        
        // Remove event listeners for fill/cancel detection
        if (chase.fillListener) {
            window.removeEventListener('order-filled', chase.fillListener);
            chase.fillListener = null;
        }
        if (chase.cancelListener) {
            window.removeEventListener('order-cancelled', chase.cancelListener);
            chase.cancelListener = null;
        }
        
        // Note: Global polling continues for other chases
        // Polling automatically skips stopped chases

        // console.log(`[CHASE-TRACKER] Monitoring stopped for ${chaseId}`);
    }

    /**
     * Handle orderbook update
     */
    async handleOrderbookUpdate(chaseId, orderbookData) {
        const chase = this.activeChases.get(chaseId);
        if (!chase || chase.status !== 'active') {
            console.log(`[CHASE-DEBUG] handleOrderbookUpdate: chase not found or not active (chaseId=${chaseId}, status=${chase?.status})`);
            return;
        }

        // Log first few orderbook updates for debugging
        if (!chase._updateCount) chase._updateCount = 0;
        chase._updateCount++;
        if (chase._updateCount <= 3) {
            console.log(`[CHASE-DEBUG] 📊 Orderbook update #${chase._updateCount} for ${chase.asset}:`, {
                bestBid: orderbookData.bestBid,
                bestAsk: orderbookData.bestAsk,
                nextCheckTime: chase.nextCheckTime ? new Date(chase.nextCheckTime).toISOString() : 'not set'
            });
        }

        // Log first orderbook update for this chase
        if (!chase._firstUpdateLogged) {
            const hasValidData = orderbookData.bestBid && orderbookData.bestAsk;
            console.log(`[CHASE-TRACKER] 📊 First orderbook update for ${chase.asset}:`, {
                bestBid: orderbookData.bestBid,
                bestAsk: orderbookData.bestAsk,
                hasValidData,
                chaseId
            });
            if (!hasValidData) {
                console.warn(`[CHASE-TRACKER] ⚠️ Orderbook data missing bids/asks for ${chase.asset} - tick size calculation will retry`);
            }
            chase._firstUpdateLogged = true;
        }

        // Range check removed - anchor constraint is now applied during order modification, not here

        // Calculate tick size from orderbook prices (retry until successful)
        if (!chase.tickSize) {
            if (orderbookData.bestBid && orderbookData.bestAsk) {
                const tickSize = this.calculateTickSizeFromOrderbook(orderbookData);

                if (tickSize) {
                    chase.tickSize = tickSize;
                    console.log(`[CHASE-TRACKER] Tick size calculated for ${chase.asset}: $${chase.tickSize}`);
                }
            }
            // If still no tick size after 10 seconds, log warning (once only)
            if (!chase.tickSize && !chase._tickSizeWarnLogged) {
                const elapsed = Date.now() - chase.startTime;
                if (elapsed > 10000) {
                    console.warn(`[CHASE-TRACKER] Still waiting for valid orderbook data to calculate tick size for ${chase.asset} (${(elapsed/1000).toFixed(0)}s elapsed)`);
                    chase._tickSizeWarnLogged = true;
                }
            }
        }
        
        // Initialize next check time if not set
        if (!chase.nextCheckTime) {
            chase.nextCheckTime = Date.now() + this.getRandomInterval(chase.settings);
            console.log(`[CHASE-TRACKER] Initial check scheduled in ${((chase.nextCheckTime - Date.now()) / 1000).toFixed(1)}s for ${chase.asset}`);
        }

        // Only check when timer expires
        const now = Date.now();
        if (now < chase.nextCheckTime) {
            return; // Not time yet
        }

        console.log(`[CHASE-TRACKER] ⏰ Timer expired, checking order position for ${chase.asset}`);
        
        try{
            // Analyze order position
            const analysis = this.analyzeOrderPosition(chase, orderbookData);
            
            // console.log(`[CHASE-TRACKER] Analysis for ${chase.asset}:`, {
            //     position: analysis.position,
            //     ticksAway: analysis.ticksAway,
            //     isValid: analysis.isValid,
            //     bestPrice: analysis.bestPrice,
            //     currentPrice: chase.currentPrice,
            //     tickDistance: chase.settings.tickDistance
            // });
            
            // Check if modification needed
            const shouldModify = this.shouldModifyOrder(chase, analysis);
            console.log(`[CHASE-TRACKER] Analysis for ${chase.asset}:`, {
                shouldModify,
                position: analysis.position,
                ticksAway: analysis.ticksAway,
                bestPrice: analysis.bestPrice,
                currentPrice: chase.currentPrice,
                tickDistance: chase.settings.tickDistance
            });
            if (shouldModify) {
                console.log(`[CHASE-TRACKER] 🔄 Modification triggered for ${chase.asset}`);
                await this.modifyOrder(chase, analysis, orderbookData);
            }
            
            // Roll new random interval for next check
            const nextInterval = this.getRandomInterval(chase.settings);
            chase.nextCheckTime = Date.now() + nextInterval;
            console.log(`[CHASE-TRACKER] Next check scheduled in ${(nextInterval / 1000).toFixed(1)}s for ${chase.asset}`);
            
        } catch (error) {
            // console.error(`[CHASE-TRACKER] Error handling orderbook update for ${chaseId}:`, error);
            // Roll new interval even on error
            const nextInterval = this.getRandomInterval(chase.settings);
            chase.nextCheckTime = Date.now() + nextInterval;
        }
    }

    /**
     * Analyze order's position in the orderbook
     */
    analyzeOrderPosition(chase, orderbookData) {
        // console.log(`[CHASE-DEBUG] analyzeOrderPosition called for ${chase.asset}`);
        // console.log(`[CHASE-DEBUG] Orderbook data:`, {
        //     hasBids: !!orderbookData?.bids,
        //     hasAsks: !!orderbookData?.asks,
        //     bestBid: orderbookData?.bestBid,
        //     bestAsk: orderbookData?.bestAsk,
        //     bidsLength: orderbookData?.bids?.length,
        //     asksLength: orderbookData?.asks?.length
        // });
        
        const isBuy = chase.side === 'B' || chase.side === 'buy';
        const levels = isBuy ? orderbookData.bids : orderbookData.asks;
        
        if (!levels || levels.length === 0) {
            // console.log(`[CHASE-TRACKER] ❌ Invalid orderbook data:`, {
            //     hasBids: !!orderbookData?.bids,
            //     hasAsks: !!orderbookData?.asks,
            //     bidsLength: orderbookData?.bids?.length,
            //     asksLength: orderbookData?.asks?.length,
            //     isBuy: isBuy,
            //     side: chase.side,
            //     asset: chase.asset
            // });
            return { position: 999, ticksAway: 999, bestPrice: null, isValid: false };
        }
        
        // Handle both array [price, size] and object {px, sz} formats
        const getPrice = (level) => {
            if (Array.isArray(level)) return parseFloat(level[0]);
            return parseFloat(level.px);
        };
        
        const bestPrice = getPrice(levels[0]);
        const currentPrice = chase.currentPrice;
        
        // Find order's position in the queue
        let position = 0;
        let ticksAway = 0;
        let foundOrder = false;
        
        for (let i = 0; i < levels.length; i++) {
            const levelPrice = getPrice(levels[i]);
            
            if (isBuy) {
                // For buy orders: higher price = better position
                if (levelPrice > currentPrice) {
                    position++;
                } else if (Math.abs(levelPrice - currentPrice) < 0.00000001) {
                    // Found our order's price level
                    position++;
                    foundOrder = true;
                    break;
                } else {
                    // Our order is behind this level
                    foundOrder = true;
                    break;
                }
            } else {
                // For sell orders: lower price = better position
                if (levelPrice < currentPrice) {
                    position++;
                } else if (Math.abs(levelPrice - currentPrice) < 0.00000001) {
                    // Found our order's price level
                    position++;
                    foundOrder = true;
                    break;
                } else {
                    // Our order is behind this level
                    foundOrder = true;
                    break;
                }
            }
        }
        
        // Calculate ticks away from best price
        ticksAway = position > 0 ? position - 1 : 0;
        
        return {
            position: position,
            ticksAway: ticksAway,
            bestPrice: bestPrice,
            isValid: true
        };
    }

    /**
     * Check if order should be modified
     */
    shouldModifyOrder(chase, analysis) {
        // console.log(`[CHASE-DEBUG] shouldModifyOrder called - chaseId: ${chase.chaseId}, asset: ${chase.asset}`);
        // console.log(`[CHASE-DEBUG] Chase object keys:`, Object.keys(chase));
        // console.log(`[CHASE-DEBUG] chase.tickSize value: ${chase.tickSize}, type: ${typeof chase.tickSize}`);
        
        // Skip if already modifying (prevent parallel modifications)
        if (chase.isModifying) {
            // console.log(`[CHASE-DEBUG] ⏸️ Already modifying, skipping this check`);
            return false;
        }
        
        // Validate analysis data
        if (!analysis.isValid || analysis.position === 999 || analysis.ticksAway === 999) {
            // console.log(`[CHASE-DEBUG] ❌ Invalid analysis - isValid: ${analysis.isValid}, position: ${analysis.position}, ticksAway: ${analysis.ticksAway}`);
            // console.log(`[CHASE-TRACKER] Invalid analysis data, skipping modification`);
            return false;
        }
        
        // Ensure tick size is calculated
        if (!chase.tickSize) {
            // console.log(`[CHASE-DEBUG] ❌ No tick size - chase.tickSize: ${chase.tickSize}`);
            // console.log(`[CHASE-TRACKER] Tick size not calculated yet, skipping modification`);
            return false;
        }
        
        // console.log(`[CHASE-DEBUG] ✅ Passed initial checks, calculating distance...`);
        
        // Calculate actual distance from best price in ticks
        const isBuy = chase.side === 'B' || chase.side === 'buy';
        const bestPrice = isBuy ? analysis.bestPrice : analysis.bestPrice; // bestBid for buy, bestAsk for sell
        
        // console.log(`[CHASE-DEBUG] Distance calculation inputs:`, {
        //     isBuy: isBuy,
        //     side: chase.side,
        //     bestPrice: bestPrice,
        //     currentPrice: chase.currentPrice,
        //     tickSize: chase.tickSize,
        //     priceDiff: Math.abs(bestPrice - chase.currentPrice)
        // });
        
        let actualDistance, targetDistance, tolerance;

        if (chase.settings.isPercent && chase.settings.percentDistance) {
            // Percentage mode: calculate actual distance as percentage of best price
            actualDistance = (Math.abs(bestPrice - chase.currentPrice) / bestPrice) * 100;
            targetDistance = chase.settings.percentDistance;
            tolerance = targetDistance * 0.2; // 20% of target percentage as tolerance
        } else {
            // Tick mode: calculate distance in ticks
            actualDistance = Math.abs(bestPrice - chase.currentPrice) / chase.tickSize;
            targetDistance = chase.settings.tickDistance;
            tolerance = 2; // 2 tick tolerance
        }

        const drift = Math.abs(actualDistance - targetDistance);
        
        // console.log(`[CHASE-DEBUG] Drift calculation: actualDistance=${actualDistance}, targetDistance=${targetDistance}, drift=${drift}`);
        
        // console.log(`[CHASE-TRACKER] Distance check:`, {
        //     actualDistance: actualDistance.toFixed(1),
        //     targetDistance: targetDistance,
        //     drift: drift.toFixed(1),
        //     tolerance: tolerance,
        //     needsModification: drift > tolerance
        // });
        
        // Only modify if we've drifted more than tolerance
        if (drift <= tolerance) {
            // console.log(`[CHASE-DEBUG] ❌ Drift within tolerance: ${drift} <= ${tolerance}`);
            return false;
        }
        
        // console.log(`[CHASE-DEBUG] ✅ Drift exceeds tolerance: ${drift} > ${tolerance}`);
        
        // No frequency check needed - timer already handles this
        
        // console.log(`[CHASE-DEBUG] ✅✅✅ ALL CHECKS PASSED - SHOULD MODIFY ORDER`);
        return true;
    }


    /**
     * Start global polling loop (NEW SIMPLIFIED SYSTEM)
     * Replaces delta monitoring + verification loop
     * Polls every 1 second, checks all chases in single API call
     */
    startPolling() {
        if (this.globalPollingInterval) return;
        
        console.log('[CHASE-TRACKER] Starting global polling loop (1s interval)');
        
        this.globalPollingInterval = setInterval(async () => {
            if (this.activeChases.size === 0) return;
            
            try {
                const allOrders = await window.BrowserHyperliquidClient.getUserOpenOrders();
                
                for (const [chaseId, chase] of this.activeChases.entries()) {
                    if (chase.status !== 'active') continue;
                    
                    await this.checkChaseStatus(chase, allOrders);
                }
            } catch (error) {
                console.warn('[CHASE-TRACKER] Polling error:', error.message);
            }
        }, 1000);
    }

    /**
     * Check single chase status against order list
     */
    async checkChaseStatus(chase, allOrders) {
        // CRITICAL: Skip polling if order is being modified to prevent race condition
        if (chase.isModifying) {
            console.log(`[CHASE-TRACKER] ⏸️ Skipping poll for ${chase.asset} during modification`);
            return;
        }
        
        const assetOrders = allOrders.filter(o => o.coin === chase.asset);
        
        const matchResult = this.matchOrder(chase, assetOrders);
        
        if (matchResult.found) {
            const order = matchResult.order;
            
            const orderSz = parseFloat(order.sz);
            if (orderSz < chase.size) {
                this.handlePartialFill(chase, order, orderSz);
            }
            
            chase.missingCount = 0;
            
        } else {
            chase.missingCount = (chase.missingCount || 0) + 1;
            
            // CRITICAL: Threshold set to 10 (1s polls × 10 = 10s before termination)
            console.warn(`[CHASE-TRACKER] ⚠️ Order NOT FOUND [${chase.missingCount}/10]: ${chase.asset} ${chase.side}, OID history: [${chase.oidHistory.join(', ')}], Current OID: ${chase.currentOid}`);
            console.warn(`[CHASE-TRACKER] Available ${chase.asset} orders in API:`, assetOrders.map(o => `OID:${o.oid} Side:${o.side} Sz:${o.sz} Px:${o.limitPx}`));
            
            // FRESH API VERIFICATION - Make new API call to verify order doesn't exist
            console.warn(`[CHASE-DEBUG] OID History Types:`, chase.oidHistory.map(oid => `${oid} (${typeof oid})`));
            console.warn(`[CHASE-DEBUG] API OID Types:`, assetOrders.map(o => `${o.oid} (${typeof o.oid})`));
            console.warn(`[CHASE-DEBUG] OID Comparisons:`);
            for (const histOid of chase.oidHistory) {
                for (const apiOrder of assetOrders) {
                    const match = String(histOid) === String(apiOrder.oid);
                    console.warn(`  ${histOid} === ${apiOrder.oid} ? ${match}`);
                }
            }
            
            if (chase.missingCount >= 10) {
                // FINAL VERIFICATION: Fresh API call before stopping
                console.error(`[CHASE-TRACKER] ❌ Performing FINAL verification before stopping chase...`);
                try {
                    const freshOrders = await window.BrowserHyperliquidClient.getUserOpenOrders();
                    const freshAssetOrders = freshOrders.filter(o => o.coin === chase.asset);
                    console.error(`[CHASE-TRACKER] FINAL CHECK - Fresh API returned ${freshAssetOrders.length} ${chase.asset} orders`);
                    console.error(`[CHASE-TRACKER] FINAL CHECK - Looking for OIDs:`, chase.oidHistory);
                    console.error(`[CHASE-TRACKER] FINAL CHECK - Available OIDs:`, freshAssetOrders.map(o => String(o.oid)));
                    
                    const finalMatch = freshAssetOrders.find(o => chase.oidHistory.includes(String(o.oid)));
                    if (finalMatch) {
                        console.error(`[CHASE-TRACKER] 🔍 FINAL CHECK FOUND ORDER! OID: ${finalMatch.oid}, resetting missingCount`);
                        chase.missingCount = 0;
                        return;
                    }
                } catch (e) {
                    console.error(`[CHASE-TRACKER] Final verification API call failed:`, e.message);
                }
                
                console.error(`[CHASE-TRACKER] ❌ Order not found after 10 polls + final verification: ${chase.asset} ${chase.side}, ending chase`);
                await this.stopChase(chase.chaseId, 'order_not_found');
            }
        }
    }

    /**
     * Match order using OID-primary + fallback strategy
     * Returns: { found: boolean, order: object|null }
     */
    matchOrder(chase, assetOrders) {
        // PRIMARY MATCH: Check OID history (exact match)
        for (const order of assetOrders) {
            const oidStr = String(order.oid);
            if (chase.oidHistory.includes(oidStr)) {
                return { found: true, order: order };
            }
        }
        
        // FALLBACK MATCH: Verify by characteristics (DO NOT MUTATE OID HISTORY)
        for (const order of assetOrders) {
            const oidStr = String(order.oid);
            
            // SAFETY: Prevent OID cross-contamination - don't match if another chase owns this OID
            const oidTaken = Array.from(this.activeChases.values()).some(c => 
                c.chaseId !== chase.chaseId && c.oidHistory.includes(oidStr)
            );
            if (oidTaken) {
                continue;
            }
            
            const assetMatch = order.coin === chase.asset;
            const directionMatch = order.side === chase.side;
            
            // EXACT price match - we track exact prices in priceHistory
            const orderPx = parseFloat(order.limitPx);
            const priceMatch = chase.priceHistory.includes(orderPx);
            
            const orderSz = parseFloat(order.sz);
            const sizeExact = Math.abs(orderSz - chase.size) < 0.000001;
            const sizePartial = orderSz > 0 && orderSz < chase.size;
            const sizeMatch = sizeExact || sizePartial;
            
            if (assetMatch && directionMatch && priceMatch && sizeMatch) {
                // CRITICAL: DO NOT add OID to history from fallback match
                // OIDs are only added when: (1) chase starts, (2) order modified with API response
                return { found: true, order: order };
            }
        }
        
        return { found: false, order: null };
    }

    /**
     * Handle partial fill detection
     */
    handlePartialFill(chase, order, newSize) {
        const fillAmount = chase.size - newSize;
        const fillPercent = (fillAmount / chase.size * 100).toFixed(1);
        
        console.log(`[CHASE-TRACKER] 🔸 Partial fill: ${chase.asset} - ${fillAmount} filled (${fillPercent}%), ${newSize} remaining`);
        
        chase.size = newSize;
        
        if (!chase.sizeHistory) {
            chase.sizeHistory = [chase.originalSize];
        }
        if (!chase.sizeHistory.includes(newSize)) {
            chase.sizeHistory.push(newSize);
            if (chase.sizeHistory.length > 5) {
                chase.sizeHistory.shift();
            }
        }
        
        if (!chase.fillEvents) {
            chase.fillEvents = [];
        }
        chase.fillEvents.push({
            timestamp: Date.now(),
            filled: fillAmount,
            remaining: newSize,
            fillPercent: parseFloat(fillPercent)
        });
        
        this.saveState();
        
        window.dispatchEvent(new CustomEvent('chase-partial-fill', {
            detail: {
                chaseId: chase.chaseId,
                asset: chase.asset,
                direction: chase.direction,
                filled: fillAmount,
                remaining: newSize,
                fillPercent: fillPercent
            }
        }));
    }

    /**
     * Modify order (cancel + recreate)
     */
    async modifyOrder(chase, analysis, orderbookData) {
        // Set modification lock
        chase.isModifying = true;

        // Check if client is initialized
        if (!window.BrowserHyperliquidClient || !window.BrowserHyperliquidClient.isInitialized) {
            console.warn(`[CHASE-TRACKER] ⚠️ Client not initialized, skipping modification for ${chase.asset}`);
            chase.isModifying = false;
            return;
        }

        // Check if chase was stopped before we started
        if (chase.status === 'stopped') {
            chase.isModifying = false;
            return;
        }

        const modifyStartTime = Date.now();
        console.log(`[CHASE-TRACKER] 🔧 modifyOrder called for ${chase.asset}`, {
            currentPrice: chase.currentPrice,
            bestPrice: analysis.bestPrice,
            side: chase.side,
            size: chase.size
        });

        // CRITICAL: Set isModifying flag to pause polling during modification
        chase.isModifying = true;
        console.log(`[CHASE-TRACKER] 🔒 Pausing polling for ${chase.asset} during modification`);

        try {

            // STEP 1: Verify order still exists by ASSET + SIZE + SIDE (not OID)
            console.log(`[CHASE-DEBUG] STEP 1: Fetching open orders... (t+${Date.now() - modifyStartTime}ms)`);
            // console.log(`[CHASE-DEBUG] STEP 1: Verifying order exists with FRESH API call...`);
            // console.log(`[CHASE-TRACKER] Looking for order: ${chase.asset} ${chase.side} ${chase.size}`);
            
            // Find order by asset, size, and side (OID changes on each modification)
            // Retry up to 3 times with delays to allow API to update
            let order = null;
            let attempts = 0;
            const maxAttempts = 3;
            
            // console.log(`[CHASE-DEBUG] Using fresh API calls (not cached tracker) - side format: '${chase.side}'`);
            
            while (!order && attempts < maxAttempts) {
                attempts++;
                
                if (attempts > 1) {
                    // console.log(`[CHASE-DEBUG] Retry attempt ${attempts}/${maxAttempts}, waiting 500ms...`);
                    await new Promise(resolve => setTimeout(resolve, 500));
                }
                
                // Get fresh orders from API
                // For HIP-3 assets, query only the specific DEX (2 API calls)
                // For regular assets, query main DEX only (2 API calls)
                const isHip3 = chase.asset.includes(':');
                let freshOrders;
                
                if (isHip3) {
                    // Extract DEX name from asset (e.g., "xyz:PERP" -> "xyz")
                    const dexName = chase.asset.split(':')[0];
                    // Direct API call to specific DEX only
                    freshOrders = await window.BrowserHyperliquidClient.infoClient.openOrders({ 
                        user: window.BrowserHyperliquidClient.publicAddress, 
                        dex: dexName 
                    });
                } else {
                    // Regular asset - main DEX only
                    freshOrders = await window.BrowserHyperliquidClient.getUserOpenOrders();
                }
                // console.log(`[CHASE-DEBUG] Fresh API returned ${freshOrders?.length || 0} total open orders (HIP-3: ${isHip3})`);
                
                for (const apiOrder of freshOrders) {
                    const orderSize = parseFloat(apiOrder.sz);
                    const expectedSize = chase.size;
                    // Match if: exact match (1% tolerance) OR partial fill (order size < expected)
                    const sizeMatch = this._sizesMatch(orderSize, expectedSize);
                    const isPartialFill = orderSize > 0 && orderSize < expectedSize * 0.99;

                    if (apiOrder.coin === chase.asset &&
                        (sizeMatch || isPartialFill) &&
                        apiOrder.side === chase.side) {
                        
                        // CRITICAL: Verify this order belongs to THIS chase (prevent order stealing)
                        if (!chase.oidHistory.includes(String(apiOrder.oid))) {
                            console.log(`[CHASE-DEBUG] Skipping order OID ${apiOrder.oid} - belongs to another chase`);
                            continue;
                        }
                        
                        order = apiOrder;

                        // Update chase size if partial fill detected
                        if (isPartialFill) {
                            console.log(`[CHASE-TRACKER] ⚠️ Partial fill detected: ${chase.asset} size ${expectedSize} → ${orderSize}`);
                            chase.size = orderSize;
                            this.saveState();
                        } else {
                            console.log(`[CHASE-TRACKER] Found order: oid=${apiOrder.oid}, sz=${apiOrder.sz}, expected=${expectedSize}`);
                        }
                        break;
                    }
                }
            }
            
            // console.log(`[CHASE-DEBUG] Order lookup result: ${order ? 'FOUND' : 'NOT FOUND'} (after ${attempts} attempts)`);
            // console.log(`[CHASE-DEBUG] Search criteria: asset=${chase.asset}, size=${chase.size}, side=${chase.side}`);
            // console.log(`[CHASE-DEBUG] Fresh API total orders: ${(await window.BrowserHyperliquidClient.getUserOpenOrders()).length}`);
            
            console.log(`[CHASE-DEBUG] STEP 1 done (t+${Date.now() - modifyStartTime}ms)`);
            console.log(`[CHASE-TRACKER] Order lookup result for ${chase.asset}:`, {
                found: !!order,
                attempts,
                searchCriteria: { asset: chase.asset, size: chase.size, side: chase.side },
                foundOid: order?.oid
            });

            // Check if chase was stopped during order lookup
            if (chase.status === 'stopped') {
                return;
            }

            if (!order) {
                console.log(`[CHASE-TRACKER] ❌ Order not found, stopping chase`);
                const stopResult = await this.stopChase(chase.chaseId, 'order_filled');
                
                if (!stopResult.success) {
                    // console.log(`[CHASE-TRACKER] ⚠️ stopChase verification found order still exists, continuing chase`);
                    // Order actually still exists, don't terminate
                    return;
                }
                
                // console.log(`[CHASE-TRACKER] ✅ Chase stopped and verified`);
                return;
            }
            
            // Extract OID from found order for cancellation
            const currentOid = order.oid;
            // console.log(`[CHASE-DEBUG] ✅ Order verified, OID: ${currentOid}, proceeding to price calculation`);
            
            // STEP 2: Calculate new price using tick distance formula
            // console.log(`[CHASE-DEBUG] STEP 2: Calculating new price...`);
            const isBuy = chase.side === 'B' || chase.side === 'buy';
            
            // Ensure tick size is calculated
            if (!chase.tickSize) {
                console.log(`[CHASE-DEBUG] ❌ CRITICAL: No tick size in modifyOrder for ${chase.asset}`);
                return;
            }
            
            // Get best bid/ask from orderbook data
            const bestBid = parseFloat(orderbookData.bestBid);
            const bestAsk = parseFloat(orderbookData.bestAsk);
            
            // console.log(`[CHASE-DEBUG] Orderbook prices - bestBid: ${bestBid}, bestAsk: ${bestAsk}`);
            
            if (!bestBid || !bestAsk) {
                console.log(`[CHASE-DEBUG] ❌ Missing orderbook data for ${chase.asset}: bestBid=${bestBid}, bestAsk=${bestAsk}`);
                return;
            }
            
            // Get asset index to cache szDecimals for proper rounding
            await window.BrowserHyperliquidClient.getAssetIndex(chase.asset);
            
            // Calculate target price: distance from current market price (ticks or percentage)
            let rawTarget;

            if (chase.settings.isPercent && chase.settings.percentDistance) {
                // Percentage mode: calculate offset as percentage of market price
                const currentMarketPrice = isBuy ? bestBid : bestAsk;
                const percentOffset = currentMarketPrice * (chase.settings.percentDistance / 100);
                if (isBuy) {
                    rawTarget = currentMarketPrice - percentOffset;
                } else {
                    rawTarget = currentMarketPrice + percentOffset;
                }
            } else {
                // Tick mode: use tick distance
                const tickOffset = chase.settings.tickDistance * chase.tickSize;

                if (chase.settings.aggressive) {
                    // AGGRESSIVE MODE: Cross the spread for faster fills
                    // Use OPPOSITE side as reference to ensure we cross the spread
                    if (isBuy) {
                        // For buy orders: start at bestAsk, add ticks to go INTO asks (immediate fill)
                        rawTarget = bestAsk + tickOffset;
                    } else {
                        // For sell orders: start at bestBid, subtract ticks to go INTO bids (immediate fill)
                        rawTarget = bestBid - tickOffset;
                    }
                } else {
                    // PASSIVE MODE: Stay on book (default for EVGrid)
                    const currentMarketPrice = isBuy ? bestBid : bestAsk;
                    if (isBuy) {
                        // For buy orders: tickDistance ticks BELOW market (stay on bid side)
                        rawTarget = currentMarketPrice - tickOffset;
                    } else {
                        // For sell orders: tickDistance ticks ABOVE market (stay on ask side)
                        rawTarget = currentMarketPrice + tickOffset;
                    }
                }
            }
            
            // Round to valid tick size (uses cached szDecimals from getAssetIndex)
            const newPrice = window.BrowserHyperliquidClient.roundToTickSize(rawTarget);
            
            // console.log(`[CHASE-DEBUG] Price after rounding: ${newPrice}`);
            // console.log(`[CHASE-TRACKER] Price calculation:`, {
            //     side: isBuy ? 'BUY' : 'SELL',
            //     bestBid: bestBid,
            //     bestAsk: bestAsk,
            //     tickSize: chase.tickSize,
            //     tickDistance: chase.settings.tickDistance,
            //     rawTarget: rawTarget,
            //     newPrice: newPrice,
            //     oldPrice: chase.currentPrice,
            //     priceChange: (newPrice - chase.currentPrice).toFixed(6)
            // });
            
            // Check anchor constraint (EV Grid feature)
            if (chase.settings.useAnchor && chase.settings.anchorPrice) {
                const anchorPrice = chase.settings.anchorPrice;
                
                if (isBuy && newPrice >= anchorPrice) {
                    // BUY order would cross above anchor - skip this modification
                    console.log(`[CHASE-DEBUG] ⚓ BUY order ${newPrice} would cross anchor ${anchorPrice}, skipping modification`);
                    return; // Don't place order, but keep chase alive
                }

                if (!isBuy && newPrice <= anchorPrice) {
                    // SELL order would cross below anchor - skip this modification
                    console.log(`[CHASE-DEBUG] ⚓ SELL order ${newPrice} would cross anchor ${anchorPrice}, skipping modification`);
                    return; // Don't place order, but keep chase alive
                }
            }

            // Check price limit constraint (user-defined upper/lower bound)
            if (chase.settings.rangePrice !== null && chase.settings.rangePrice > 0) {
                const rangePrice = chase.settings.rangePrice;
                const rangeType = chase.settings.rangeType;

                if (rangeType === 'upper' && newPrice >= rangePrice) {
                    // Long order: price exceeded upper limit - stop chase
                    console.log(`[CHASE-TRACKER] 🛑 Price ${newPrice} exceeded upper limit ${rangePrice}, stopping chase`);
                    await this.stopChase(chase.chaseId, 'price_limit_reached');
                    return;
                }

                if (rangeType === 'lower' && newPrice <= rangePrice) {
                    // Short order: price dropped below lower limit - stop chase
                    console.log(`[CHASE-TRACKER] 🛑 Price ${newPrice} below lower limit ${rangePrice}, stopping chase`);
                    await this.stopChase(chase.chaseId, 'price_limit_reached');
                    return;
                }
            }

            // STEP 3: Cancel old order (using OID from the order we just found)
            console.log(`[CHASE-DEBUG] STEP 3: Canceling order OID ${currentOid}... (t+${Date.now() - modifyStartTime}ms)`);

            const cancelResult = await window.BrowserHyperliquidClient.cancelOrder({
                orderId: currentOid,
                asset: chase.asset
            });

            console.log(`[CHASE-DEBUG] STEP 3 done (t+${Date.now() - modifyStartTime}ms)`);
            console.log(`[CHASE-DEBUG] Cancel result:`, cancelResult);
            console.log(`[CHASE-DEBUG] Cancel success: ${cancelResult.success}`);
            
            if (!cancelResult.success) {
                console.log(`[CHASE-DEBUG] ❌ Cancel failed`);
                console.error(`[CHASE-TRACKER] Cancel failed:`, cancelResult.error);

                // Check if order was already filled (error might be string or object)
                const errorStr = typeof cancelResult.error === 'string'
                    ? cancelResult.error
                    : JSON.stringify(cancelResult.error);

                if (errorStr && errorStr.toLowerCase().includes('filled')) {
                    // console.log(`[CHASE-TRACKER] Cancel failed because order was filled - stopping chase`);
                    await this.stopChase(chase.chaseId, 'order_filled');
                }
                return;
            }

            // console.log(`[CHASE-DEBUG] ✅ Order canceled successfully`);
            // console.log(`[CHASE-TRACKER] ✅ Order canceled`);

            // Check if chase was stopped during cancellation
            if (chase.status === 'stopped') {
                return;
            }

            // STEP 4: Create new order at new price
            console.log(`[CHASE-DEBUG] STEP 4: Placing new order at ${newPrice}... (t+${Date.now() - modifyStartTime}ms)`);
            const newOrderResult = await window.BrowserHyperliquidClient.executeTradingOrder({
                asset: chase.asset,
                orderSide: isBuy ? 'buy' : 'sell',
                size: chase.size,
                price: newPrice,
                orderType: 'limit',
                timeInForce: 'GTC',
                reduceOnly: false
            });

            console.log(`[CHASE-DEBUG] STEP 4 done (t+${Date.now() - modifyStartTime}ms)`);
            console.log(`[CHASE-DEBUG] New order result:`, newOrderResult);
            
            if (!newOrderResult.success) {
                console.error(`[CHASE-TRACKER] ❌ Failed to recreate order after cancel:`, newOrderResult.error);

                // Dispatch error event for UI notification
                window.dispatchEvent(new CustomEvent('chase-modification-failed', {
                    detail: {
                        chaseId: chase.chaseId,
                        asset: chase.asset,
                        side: chase.side,
                        size: chase.size,
                        error: newOrderResult.error,
                        message: 'Order cancelled but new order failed - position has NO open order'
                    }
                }));

                // Manual cleanup (DO NOT call stopChase - order already cancelled)
                chase.status = 'stopped';
                chase.endTime = Date.now();
                chase.endReason = 'modification_failed_orphan';
                chase.isStopping = false;

                this.stopMonitoring(chase.chaseId);
                this.chaseHistory.push(chase);
                this.activeChases.delete(chase.chaseId);
                this.disconnectOrderbookIfUnused(chase.asset);
                this.saveState();

                // Dispatch stop event
                window.dispatchEvent(new CustomEvent('chase-stopped', {
                    detail: {
                        chaseId: chase.chaseId,
                        reason: 'modification_failed_orphan',
                        finalOid: chase.currentOid,
                        modCount: chase.modificationCount
                    }
                }));

                return;
            }
            
            console.log(`[CHASE-DEBUG] ✅ New order created successfully`);

            // STEP 5: Verify new order was created and extract OID
            console.log(`[CHASE-DEBUG] STEP 5: Extracting OID from response...`);
            console.log(`[CHASE-DEBUG] Full result structure:`, JSON.stringify(newOrderResult.result, null, 2));

            // Try multiple paths to extract OID (SDK structure varies)
            let newOid = newOrderResult.result?.response?.data?.statuses?.[0]?.resting?.oid;
            if (!newOid) {
                newOid = newOrderResult.result?.response?.data?.statuses?.[0]?.filled?.oid;
            }
            if (!newOid) {
                newOid = newOrderResult.orderId || newOrderResult.oid;
            }

            console.log(`[CHASE-DEBUG] Extracted OID: ${newOid}`);

            if (!newOid) {
                console.error(`[CHASE-DEBUG] ❌ No OID found in response!`);
                console.error(`[CHASE-DEBUG] Available paths:`, {
                    'result.response.data.statuses': newOrderResult.result?.response?.data?.statuses,
                    'orderId': newOrderResult.orderId,
                    'oid': newOrderResult.oid
                });
                await this.stopChase(chase.chaseId, 'modification_failed');
                return;
            }
            
            // console.log(`[CHASE-TRACKER] ✅ New order created successfully`);

            // STEP 6: Update chase state with NEW OID
            // console.log(`[CHASE-DEBUG] STEP 6: Updating chase state...`);
            const oldOid = chase.currentOid;
            chase.currentOid = String(newOid);
            chase.oidHistory.push(String(newOid));
            
            // Keep last 5 OIDs
            if (chase.oidHistory.length > 5) {
                chase.oidHistory.shift();
            }
            
            chase.currentPrice = newPrice;
            chase.priceHistory.push(newPrice);
            
            // Keep last 5 prices
            if (chase.priceHistory.length > 5) {
                chase.priceHistory.shift();
            }
            
            chase.lastModification = Date.now();
            chase.modificationCount++;
            
            // CRITICAL: Reset missingCount after successful modification
            chase.missingCount = 0;
            
            console.log(`[CHASE-TRACKER] Updated tracking: OID ${newOid}, price ${newPrice}`);
            
            // STEP 7: Save state immediately (critical for next poll)
            // console.log(`[CHASE-DEBUG] STEP 7: Saving state...`);
            this.saveState();
            
            // CRITICAL: Clear isModifying flag to resume polling
            chase.isModifying = false;
            console.log(`[CHASE-TRACKER] 🔓 Resuming polling for ${chase.asset} after successful modification`);
            
            // STEP 7.5: Poll specific DEX to immediately get new order
            // Prevents order from disappearing for 0-15 seconds until next poll
            // Detect HIP-3 vs Main to poll correct DEX only (efficient 2 API calls)
            const isHip3 = chase.asset.includes(':');
            const dexName = isHip3 ? chase.asset.split(':')[0] : 'main';
            
            if (window.BrowserPositionTracker) {
                // Don't await - let it run async to not slow down chase logic
                // Poll position data to sync state
                window.BrowserPositionTracker.pollData().catch(err => {
                    // console.error(`[CHASE-TRACKER] Poll after modification failed:`, err);
                });
            }
            
            // STEP 8: Dispatch event
            // console.log(`[CHASE-DEBUG] STEP 8: Dispatching event...`);
            window.dispatchEvent(new CustomEvent('chase-order-modified', {
                detail: {
                    chaseId: chase.chaseId,
                    oldOid: currentOid,
                    newOid: newOid,
                    newPrice: newPrice,
                    modCount: chase.modificationCount
                }
            }));
            
            // console.log(`[CHASE-TRACKER] ✅ Order modified successfully (mod #${chase.modificationCount})`);
            
        } catch (error) {
            console.log(`[CHASE-DEBUG] ❌❌❌ EXCEPTION in modifyOrder:`, error);
            console.error(`[CHASE-TRACKER] Error modifying order:`, error);
            console.error(error.stack);
            
            // Check if error indicates order was filled
            if (error.message && error.message.includes('filled')) {
                await this.stopChase(chase.chaseId, 'order_filled');
            }
        } finally {
            // Release modification lock
            chase.isModifying = false;
        }
        
        // console.log(`[CHASE-DEBUG] ═══════════════════════════════════════`);
        // console.log(`[CHASE-DEBUG] MODIFY ORDER COMPLETED`);
        // console.log(`[CHASE-DEBUG] ═══════════════════════════════════════`);
    }

    /**
     * Get active chase for an order (by OID or size matching)
     */
    getChaseForOrder(order) {
        // EXCLUDE reduce-only orders (TP/SL) from chase highlighting
        if (order.reduceOnly || order.isPositionTpsl) {
            return null;
        }
        
        const orderOid = String(order.oid);
        const orderAsset = order.coin || order.asset;
        const orderSize = parseFloat(order.sz || order.size);
        
        // Normalize side (handle both 'B'/'A' and 'BUY'/'SELL')
        let orderSide = order.side;
        if (orderSide === 'BUY') orderSide = 'B';
        if (orderSide === 'SELL') orderSide = 'A';
        
        // Strategy 1: Exact current OID match
        for (const [chaseId, chase] of this.activeChases.entries()) {
            if (String(chase.currentOid) === orderOid) {
                return { chaseId, chase, method: 'exact_oid' };
            }
        }
        
        // Strategy 2: Historical OID match
        for (const [chaseId, chase] of this.activeChases.entries()) {
            if (chase.oidHistory.includes(orderOid)) {
                return { chaseId, chase, method: 'historical_oid' };
            }
        }
        
        // Strategy 3: Asset + Side + Size match (includes partial fills)
        // CRITICAL: This is a READ-ONLY query function for UI highlighting
        // DO NOT mutate chase state here - OID updates handled in checkChaseStatus/modifyOrder
        for (const [chaseId, chase] of this.activeChases.entries()) {
            const assetMatch = chase.asset === orderAsset;
            const sideMatch = chase.side === orderSide;
            const sizeMatch = this._sizesMatch(orderSize, chase.size);
            const isPartialFill = orderSize > 0 && orderSize < chase.size * 0.99;

            if (assetMatch && sideMatch && (sizeMatch || isPartialFill)) {
                return { chaseId, chase, method: 'asset_side_size' };
            }
        }
        
        return null;
    }

    /**
     * Save state to localStorage
     */
    saveState() {
        try {
            // Save active chases
            const chasesObj = {};
            for (const [chaseId, chase] of this.activeChases.entries()) {
                // Remove non-serializable properties (functions)
                const { orderbookListener, fillListener, cancelListener, ...serializable } = chase;
                chasesObj[chaseId] = serializable;
            }
            localStorage.setItem('activeChases', JSON.stringify(chasesObj));
            
            // Save history (keep last 50)
            const recentHistory = this.chaseHistory.slice(-50);
            localStorage.setItem('chaseHistory', JSON.stringify(recentHistory));
            
        } catch (error) {
            // console.error('[CHASE-TRACKER] Error saving state:', error);
        }
    }

    /**
     * Restore state from localStorage
     */
    restoreState() {
        try {
            // Restore active chases
            const saved = localStorage.getItem('activeChases');
            if (saved) {
                const chasesObj = JSON.parse(saved);
                console.log(`[CHASE-TRACKER] 🔄 Restoring ${Object.keys(chasesObj).length} chases from localStorage`);
                for (const [chaseId, chase] of Object.entries(chasesObj)) {
                    console.log(`[CHASE-TRACKER] 🔄 Restored chase: ${chase.asset} ${chase.side} size=${chase.size} freq=${chase.settings?.frequencyRangeMin}s`);
                    this.activeChases.set(chaseId, chase);
                    
                    // Re-create event listeners for instant fill/cancel detection
                    const fillListener = (event) => {
                        const { orderId } = event.detail;
                        if (chase.oidHistory && chase.oidHistory.includes(String(orderId))) {
                            this.stopChase(chaseId, 'order_filled');
                        }
                    };
                    
                    const cancelListener = (event) => {
                        const { orderId } = event.detail;
                        if (chase.oidHistory && chase.oidHistory.includes(String(orderId))) {
                            this.stopChase(chaseId, 'order_cancelled');
                        }
                    };
                    
                    window.addEventListener('order-filled', fillListener);
                    window.addEventListener('order-cancelled', cancelListener);
                    
                    // Store listeners for cleanup
                    chase.fillListener = fillListener;
                    chase.cancelListener = cancelListener;
                    
                    // Reset missing count for restored chase
                    chase.missingCount = 0;
                    
                    // Reconnect to orderbook and resume monitoring (async)
                    (async () => {
                        // Set restore time for tracking
                        chase.restoreTime = Date.now();

                        // Wait for client to be initialized (max 10 seconds)
                        let waitAttempts = 0;
                        while ((!window.BrowserHyperliquidClient || !window.BrowserHyperliquidClient.isInitialized) && waitAttempts < 20) {
                            await new Promise(resolve => setTimeout(resolve, 500));
                            waitAttempts++;
                        }

                        if (!window.BrowserHyperliquidClient || !window.BrowserHyperliquidClient.isInitialized) {
                            console.warn(`[CHASE-TRACKER] ⚠️ Client not initialized after 10s, removing restored chase for ${chase.asset}`);
                            this._removeRestoredChase(chaseId, chase);
                            return;
                        }

                        // RECONCILE: Update OID with actual exchange order (handles tab crash recovery)
                        try {
                            const isHip3 = chase.asset.includes(':');
                            let freshOrders;

                            if (isHip3) {
                                // Query specific DEX only (1 API call)
                                const dexName = chase.asset.split(':')[0];
                                freshOrders = await window.BrowserHyperliquidClient.infoClient.openOrders({
                                    user: window.BrowserHyperliquidClient.publicAddress,
                                    dex: dexName
                                });
                            } else {
                                freshOrders = await window.BrowserHyperliquidClient.getUserOpenOrders();
                            }

                            // Match by asset + side + size (1% tolerance OR partial fill)
                            let matchedOrder = null;
                            for (const o of (freshOrders || [])) {
                                if (o.coin === chase.asset && o.side === chase.side) {
                                    const orderSize = parseFloat(o.sz);
                                    const sizeMatch = this._sizesMatch(orderSize, chase.size);
                                    const isPartialFill = orderSize > 0 && orderSize < chase.size * 0.99;

                                    if (sizeMatch || isPartialFill) {
                                        matchedOrder = o;
                                        // Update chase size if partial fill detected during restore
                                        if (isPartialFill) {
                                            console.log(`[CHASE-TRACKER] ⚠️ Restore detected partial fill: ${chase.asset} size ${chase.size} → ${orderSize}`);
                                            chase.size = orderSize;
                                        }
                                        break;
                                    }
                                }
                            }

                            if (matchedOrder) {
                                if (String(matchedOrder.oid) !== String(chase.currentOid)) {
                                    console.log(`[CHASE-TRACKER] ✅ Reconciled OID for ${chase.asset}: ${chase.currentOid} -> ${matchedOrder.oid}`);
                                    chase.currentOid = String(matchedOrder.oid);
                                    if (!chase.oidHistory) chase.oidHistory = [];
                                    if (!chase.oidHistory.includes(String(matchedOrder.oid))) {
                                        chase.oidHistory.push(String(matchedOrder.oid));
                                    }
                                    this.saveState();
                                }
                                console.log(`[CHASE-TRACKER] ✅ Restored chase verified: ${chase.asset} ${chase.side} size=${chase.size}`);
                            } else {
                                // No matching order - likely filled/cancelled during crash
                                console.warn(`[CHASE-TRACKER] ⚠️ No matching order for ${chase.asset} on restore, removing chase`);
                                this._removeRestoredChase(chaseId, chase);
                                return; // Skip monitoring
                            }
                        } catch (e) {
                            // On ANY error, remove the chase instead of proceeding with stale data
                            console.warn(`[CHASE-TRACKER] ⚠️ Reconciliation failed for ${chase.asset}, removing chase:`, e.message);
                            this._removeRestoredChase(chaseId, chase);
                            return;
                        }

                        await this.connectOrderbook(chase.asset);
                        await this.startMonitoring(chaseId);

                        // Start global polling loop (handles all chases)
                        this.startPolling();
                    })().catch(err => console.error('[CHASE-TRACKER] Error restoring chase:', err));
                }
                // console.log(`[CHASE-TRACKER] Restored ${this.activeChases.size} active chases`);
            }
            
            // Restore history
            const history = localStorage.getItem('chaseHistory');
            if (history) {
                this.chaseHistory = JSON.parse(history);
                // console.log(`[CHASE-TRACKER] Restored ${this.chaseHistory.length} chase history entries`);
            }
        } catch (error) {
            // console.error('[CHASE-TRACKER] Error restoring state:', error);
        }
    }

    /**
     * Helper to clean up a restored chase that failed verification
     */
    _removeRestoredChase(chaseId, chase) {
        this.activeChases.delete(chaseId);
        if (chase.fillListener) {
            window.removeEventListener('order-filled', chase.fillListener);
        }
        if (chase.cancelListener) {
            window.removeEventListener('order-cancelled', chase.cancelListener);
        }
        this.saveState();
    }

    /**
     * Get status of all active chases
     */
    getStatus() {
        const chases = {};
        for (const [chaseId, chase] of this.activeChases.entries()) {
            chases[chaseId] = {
                chaseId: chase.chaseId,
                asset: chase.asset,
                currentOid: chase.currentOid,
                modCount: chase.modificationCount,
                status: chase.status
            };
        }
        return {
            success: true,
            activeChases: chases,
            count: this.activeChases.size
        };
    }

    /**
     * Cancel TP/SL orders for a position
     * @param {string} asset - Asset symbol
     * @param {string} side - Order side ('B' or 'A')
     * @returns {Array} - Array of cancelled order IDs
     */
    async cancelTpSlOrders(asset, side) {
        const cancelled = [];
        
        try {
            // Get fresh order data
            await window.BrowserPositionTracker.pollData();
            
            // Find TP/SL orders for this position
            const orders = Array.from(window.BrowserPositionTracker.orders.values());
            const tpSlOrders = orders.filter(order => {
                const orderAsset = order.coin || order.asset;
                const orderSide = order.side; // 'BUY' or 'SELL' from position-tracker
                const isReduceOnly = order.reduceOnly || order.orderType === 'TP' || order.orderType === 'SL';
                
                // Match asset and opposite side (TP/SL are reduce-only on opposite side)
                // side param is 'B' or 'A', need to convert to 'BUY'/'SELL' for comparison
                const oppositeSide = side === 'B' ? 'SELL' : 'BUY';
                return orderAsset === asset && orderSide === oppositeSide && isReduceOnly;
            });
            
            // console.log(`[CHASE-TRACKER] Found ${tpSlOrders.length} TP/SL orders to cancel for ${asset}`);
            
            // Cancel each TP/SL order
            for (const order of tpSlOrders) {
                const orderId = order.oid;
                // console.log(`[CHASE-TRACKER] Cancelling TP/SL order ${orderId}...`);
                
                const result = await window.BrowserHyperliquidClient.cancelOrder({
                    orderId: orderId,
                    asset: asset
                });
                
                if (result.success) {
                    cancelled.push({
                        oid: orderId,
                        price: order.limitPx || order.price,
                        type: order.orderType
                    });
                    // console.log(`[CHASE-TRACKER] ✅ Cancelled TP/SL order ${orderId}`);
                } else {
                    // console.error(`[CHASE-TRACKER] ❌ Failed to cancel TP/SL order ${orderId}:`, result.error);
                }
            }
            
        } catch (error) {
            // console.error('[CHASE-TRACKER] Error cancelling TP/SL orders:', error);
        }
        
        return cancelled;
    }

    /**
     * Place TP/SL orders when chase order fills
     * Uses ATR multiples from chase settings
     *
     * FIX (2025-12-26): Added position verification before placing TP/SL
     * Bug: Could attempt to place TP/SL for non-existent position if order was cancelled
     */
    async placeTPSLOnFill(chase) {
        try {
            const { asset, side, size, settings } = chase;
            const isBuy = side === 'B' || side === 'buy';

            // CRITICAL: Verify position exists before placing TP/SL
            // Order could have been cancelled (not filled), so we must verify
            let entryPrice = null;
            let positionSize = 0;

            try {
                // For HIP-3, query specific DEX
                const isHip3 = asset.includes(':');
                let positions;

                if (isHip3) {
                    const dexName = asset.split(':')[0];
                    const userState = await window.BrowserHyperliquidClient.getUserStateForDex(dexName);
                    positions = userState?.assetPositions || [];
                } else {
                    positions = await window.BrowserHyperliquidClient.getUserPositions();
                }

                const position = positions.find(p => p.position?.coin === asset);
                if (position && position.position?.entryPx) {
                    entryPrice = parseFloat(position.position.entryPx);
                    positionSize = Math.abs(parseFloat(position.position.szi || 0));
                }
            } catch (e) {
                console.warn('[CHASE-TRACKER] Could not fetch position for TP/SL verification:', e.message);
            }

            // If no position exists, don't place TP/SL
            if (!entryPrice || entryPrice <= 0 || positionSize === 0) {
                console.warn(`[CHASE-TRACKER] No position found for ${asset}, skipping TP/SL placement`);
                return;
            }

            console.log(`[CHASE-TRACKER] Position verified for ${asset}: entry=${entryPrice}, size=${positionSize}`);

            // Get ATR for calculations
            let atr = null;
            if (window.ATRService) {
                const atrData = await window.ATRService.getATR(asset);
                if (atrData && atrData.atr) {
                    atr = atrData.atr;
                }
            }

            if (!atr) {
                // Fallback: use 1.5% of price as ATR proxy
                atr = entryPrice * 0.015;
                // console.warn(`[CHASE-TRACKER] ATR unavailable, using fallback: ${atr.toFixed(4)}`);
            }

            const tpMultiple = settings.tpAtrMultiple || 2.0;
            const slMultiple = settings.slAtrMultiple || 1.5;

            // Calculate TP and SL prices
            let tpPrice, slPrice;
            if (isBuy) {
                // Long position: TP above, SL below
                tpPrice = entryPrice + (atr * tpMultiple);
                slPrice = entryPrice - (atr * slMultiple);
            } else {
                // Short position: TP below, SL above
                tpPrice = entryPrice - (atr * tpMultiple);
                slPrice = entryPrice + (atr * slMultiple);
            }

            // Round prices
            tpPrice = window.BrowserHyperliquidClient.roundToTickSize(tpPrice);
            slPrice = window.BrowserHyperliquidClient.roundToTickSize(slPrice);

            // console.log(`[CHASE-TRACKER] Placing TP/SL for ${asset}:`, {
            //     entry: entryPrice,
            //     atr: atr,
            //     tpMultiple: tpMultiple,
            //     slMultiple: slMultiple,
            //     tp: tpPrice,
            //     sl: slPrice,
            //     side: isBuy ? 'LONG' : 'SHORT'
            // });

            // Place TP order using proper tpslEnabled + tpPrice params
            // orderSide = entry side (buy for long), buildTPOrder flips to opposite internally
            // Use positionSize (actual size from exchange, properly rounded) instead of chase.size
            const tpResult = await window.BrowserHyperliquidClient.executeTradingOrder({
                asset,
                orderSide: isBuy ? 'buy' : 'sell', // Entry side - gets flipped for TP
                size: positionSize,    // Use actual position size (properly rounded)
                price: null,           // No entry price (TP/SL only mode)
                orderType: 'limit',
                reduceOnly: true,
                timeInForce: 'GTC',
                tpslEnabled: true,     // Tells buildSDKOrder to use buildTPOrder
                tpPrice: tpPrice,      // TP trigger price
                slPrice: null          // Not placing SL in this call
            });

            if (tpResult.success) {
                // console.log(`[CHASE-TRACKER] ✅ TP placed at ${tpPrice}`);
            } else {
                console.error(`[CHASE-TRACKER] ❌ Failed to place TP:`, tpResult.error);
            }

            // Place SL order using proper tpslEnabled + slPrice params
            // orderSide = entry side (buy for long), buildSLOrder flips to opposite internally
            const slResult = await window.BrowserHyperliquidClient.executeTradingOrder({
                asset,
                orderSide: isBuy ? 'buy' : 'sell', // Entry side - gets flipped for SL
                size: positionSize,    // Use actual position size (properly rounded)
                price: null,           // No entry price (TP/SL only mode)
                orderType: 'limit',
                reduceOnly: true,
                timeInForce: 'GTC',
                tpslEnabled: true,     // Tells buildSDKOrder to use buildSLOrder
                tpPrice: null,         // Not placing TP in this call
                slPrice: slPrice       // SL trigger price
            });

            if (slResult.success) {
                // console.log(`[CHASE-TRACKER] ✅ SL placed at ${slPrice}`);
            } else {
                // console.error(`[CHASE-TRACKER] ❌ Failed to place SL:`, slResult.error);
            }

            // Notify user
            if (window.NotificationManager) {
                window.NotificationManager.showNotification({
                    type: 'success',
                    title: '✅ TP/SL Placed',
                    message: `${asset}: TP=${tpPrice.toFixed(2)}, SL=${slPrice.toFixed(2)}`,
                    duration: 5000
                });
            }

        } catch (error) {
            // console.error('[CHASE-TRACKER] Error in placeTPSLOnFill:', error);
        }
    }

    /**
     * Get random interval from frequency range (in milliseconds)
     */
    getRandomInterval(settings) {
        const min = settings.frequencyRangeMin * 1000;
        const max = settings.frequencyRangeMax * 1000;
        const interval = Math.random() * (max - min) + min;
        console.log(`[CHASE-DEBUG] getRandomInterval: min=${settings.frequencyRangeMin}s, max=${settings.frequencyRangeMax}s, result=${(interval/1000).toFixed(1)}s`);
        return interval;
    }

    /**
     * Calculate tick size from orderbook prices
     * Uses actual price precision from the orderbook data
     */
    calculateTickSizeFromOrderbook(orderbookData) {
        if (!orderbookData || !orderbookData.bestBid || !orderbookData.bestAsk) {
            return null;
        }
        
        // Get the number of decimal places from the best bid/ask
        const bidStr = String(orderbookData.bestBid);
        const askStr = String(orderbookData.bestAsk);
        
        // Count decimals in both prices
        const bidDecimals = bidStr.includes('.') ? bidStr.split('.')[1].length : 0;
        const askDecimals = askStr.includes('.') ? askStr.split('.')[1].length : 0;
        
        // Use the maximum decimal places
        const decimals = Math.max(bidDecimals, askDecimals);
        
        // Tick size = 10^-decimals
        const tickSize = Math.pow(10, -decimals);
        
        // console.log(`[CHASE-TRACKER] Tick size calculated from orderbook: $${tickSize} (${decimals} decimals)`);
        // console.log(`[CHASE-TRACKER]   bestBid: ${bidStr} (${bidDecimals} decimals), bestAsk: ${askStr} (${askDecimals} decimals)`);
        
        return tickSize;
    }
}

// Create singleton instance
if (typeof window !== 'undefined') {
    window.BrowserChaseTracker = new BrowserChaseTracker();
    // console.log('✅ BrowserChaseTracker initialized');
}
